import { useEffect, useState, useRef } from "react";
import { useAppStore } from "@/store/appStore";

/* ── Confetti particle ─────────────────────────────────────────────────────── */
function ConfettiParticle({ color, x, delay, duration, size = 6, shape = "circle" }) {
  return (
    <div
      style={{
        position:    "absolute",
        left:        `${x}%`,
        top:         "-10px",
        width:       size,
        height:      size,
        background:  color,
        borderRadius: shape === "circle" ? "50%" : shape === "square" ? "2px" : "1px",
        animation:   `confetti-fall ${duration}s ${delay}s ease-in both`,
        transform:   `rotate(${Math.random() * 360}deg)`,
        boxShadow:   `0 0 ${size * 1.5}px ${color}`,
      }}
    />
  );
}

/* ── Expanding ring ────────────────────────────────────────────────────────── */
function PulseRing({ delay = 0, color = "rgba(139,92,246,0.5)" }) {
  return (
    <div
      className="absolute inset-0 rounded-full pointer-events-none"
      style={{
        border:    `2px solid ${color}`,
        animation: `ring-expand 1.5s ${delay}s ease-out infinite`,
      }}
    />
  );
}

/* ── Animated number counter ───────────────────────────────────────────────── */
function AnimatedNumber({ target, prefix = "+", color = "text-violet-400", size = "text-3xl" }) {
  const [displayed, setDisplayed] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    const start    = performance.now();
    const duration = 900;
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(eased * target));
      if (progress < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target]);

  return (
    <span className={`${size} font-black ${color} tabular-nums`}>
      {prefix}{displayed.toLocaleString("de-DE")}
    </span>
  );
}

const CONFETTI_COLORS = ["#8b5cf6", "#06b6d4", "#ec4899", "#fbbf24", "#22c55e", "#f97316", "#ffffff"];
const SHAPES          = ["circle", "square", "rect"];

function makeConfetti(count) {
  return Array.from({ length: count }, (_, i) => ({
    id:       i,
    color:    CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    x:        Math.random() * 100,
    delay:    Math.random() * 1.5,
    duration: 2.5 + Math.random() * 2,
    size:     4 + Math.random() * 6,
    shape:    SHAPES[Math.floor(Math.random() * SHAPES.length)],
  }));
}

export default function RewardOverlay() {
  const { reward, clearReward } = useAppStore();
  const [confetti, setConfetti] = useState([]);
  const [visible,  setVisible]  = useState(false);

  useEffect(() => {
    if (reward) {
      setConfetti(makeConfetti(40));
      setVisible(true);
      const t = setTimeout(clearReward, 4000);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [reward]);

  if (!reward && !visible) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center pointer-events-none"
      style={{ perspective: "1000px" }}
    >
      {/* Dark overlay */}
      <div
        className="absolute inset-0 pointer-events-auto"
        onClick={clearReward}
        style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
      />

      {/* Confetti rain */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {confetti.map((c) => (
          <ConfettiParticle key={c.id} {...c} />
        ))}
      </div>

      {/* Main card */}
      <div
        className="relative z-10 text-center pointer-events-auto animate-bounce-in"
        onClick={clearReward}
        style={{ filter: "drop-shadow(0 0 60px rgba(139,92,246,0.4))" }}
      >
        <div
          className="relative rounded-3xl px-10 py-8 mx-4"
          style={{
            background: "linear-gradient(135deg, rgba(19,19,58,0.97) 0%, rgba(9,9,32,0.99) 100%)",
            border:     "1px solid rgba(139,92,246,0.4)",
            boxShadow:  "0 0 0 1px rgba(255,255,255,0.04) inset, 0 30px 80px rgba(0,0,0,0.8)",
          }}
        >
          {/* Pulse rings */}
          <div className="absolute inset-0 rounded-3xl pointer-events-none">
            <PulseRing delay={0}    color="rgba(139,92,246,0.4)" />
            <PulseRing delay={0.4}  color="rgba(6,182,212,0.3)" />
            <PulseRing delay={0.8}  color="rgba(236,72,153,0.2)" />
          </div>

          {/* Glow overlay */}
          <div
            className="absolute inset-0 rounded-3xl pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.12) 0%, transparent 60%)" }}
          />

          {/* Lightning icon */}
          <div
            className="text-7xl mb-4 animate-float inline-block"
            style={{ filter: "drop-shadow(0 0 20px rgba(139,92,246,0.8))" }}
          >
            ⚡
          </div>

          {/* Title */}
          <h3
            className="text-2xl font-black mb-1"
            style={{ background: "linear-gradient(135deg, #8b5cf6, #06b6d4, #ec4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}
          >
            Belohnung!
          </h3>

          {reward.reason && (
            <p className="text-xs text-gray-400 mb-5 font-medium">{reward.reason}</p>
          )}

          {/* Reward chips */}
          <div className="flex items-center justify-center gap-4 mt-3">
            {reward.xp > 0 && (
              <div
                className="flex flex-col items-center px-6 py-4 rounded-2xl relative overflow-hidden"
                style={{
                  background: "rgba(139,92,246,0.12)",
                  border:     "1px solid rgba(139,92,246,0.35)",
                  boxShadow:  "0 0 20px rgba(139,92,246,0.2)",
                }}
              >
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center top, rgba(139,92,246,0.15), transparent 70%)" }} />
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">XP</p>
                <AnimatedNumber target={reward.xp} color="text-violet-400" size="text-3xl" />
              </div>
            )}
            {reward.coins > 0 && (
              <div
                className="flex flex-col items-center px-6 py-4 rounded-2xl relative overflow-hidden"
                style={{
                  background: "rgba(251,191,36,0.10)",
                  border:     "1px solid rgba(251,191,36,0.35)",
                  boxShadow:  "0 0 20px rgba(251,191,36,0.2)",
                }}
              >
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center top, rgba(251,191,36,0.12), transparent 70%)" }} />
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">COINS</p>
                <AnimatedNumber target={reward.coins} color="text-yellow-400" size="text-3xl" />
              </div>
            )}
          </div>

          <p className="text-[10px] text-gray-600 mt-5 font-medium">Tippen zum Schließen</p>
        </div>
      </div>
    </div>
  );
}
